import { Tooltip } from '@/shared/components/Tooltip'
import type { ProviderArtefactRow } from '@/lib/apiTypes'

/** How the org came to have the provider: a lockfile / module surfaced it (auto-discovered) or an
 *  admin added it by hand. Every row is managed either way, so this is a plain provenance mark -
 *  a green tick for auto-discovered (the platform saw it in use) - and the tooltip names the
 *  evidence. `compact` renders the glyph only (table cells). */
export function DiscoveredMark({ row, compact = false }: {
  row: Pick<ProviderArtefactRow, 'discovered' | 'usage_workspace_count' | 'referenced_module_count'>
  compact?: boolean
}) {
  const sources: string[] = []
  if (row.usage_workspace_count > 0) sources.push(`locked by ${row.usage_workspace_count} workspace${row.usage_workspace_count === 1 ? '' : 's'}`)
  if (row.referenced_module_count > 0) sources.push(`declared by ${row.referenced_module_count} module${row.referenced_module_count === 1 ? '' : 's'}`)
  const tip = row.discovered
    ? `Auto-discovered: ${sources.length ? sources.join(', ') : 'seen in a lock file or module required_providers'}. Managed, scanned and served automatically.`
    : 'Added manually.'
  const label = row.discovered ? 'Auto-discovered' : 'Added manually'
  // Hand-added is provenance, not a failure, so the labelled reading carries the words alone - a
  // cross in front of "Added manually" read as if something had gone wrong. The compact cell keeps
  // it, because there the glyph IS the answer to a yes/no column.
  const glyph = row.discovered ? '✓' : compact ? '✗' : null
  return (
    <Tooltip text={tip} placement="top">
      <span tabIndex={0} aria-label={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'help', whiteSpace: 'nowrap' }}>
        {glyph && (
          <span aria-hidden style={{ color: row.discovered ? 'var(--green)' : 'var(--text3)', fontWeight: 600, fontSize: '0.85rem' }}>
            {glyph}
          </span>
        )}
        {!compact && <span style={{ fontSize: '0.72rem', color: 'var(--text2)' }}>{label}</span>}
      </span>
    </Tooltip>
  )
}
